import { Trans, useLingui } from "@lingui/react/macro";
import { Check } from "lucide-react";
import { useRef, useState } from "react";

import {
  apiPostEvents,
  EventCategory,
  PhysicianNotifiedEventType,
} from "@/api";
import { handleError } from "@/app/errors";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui-kit/base/dialog";
import { Button } from "@/components/ui-kit/button";
import { BaseTextarea } from "@/components/ui-kit/fields/base-textarea";
import { Label } from "@/components/ui-kit/fields/label";
import { IconButton } from "@/components/ui-kit/icon-button";

import type { MonitoringRow } from "./daily-monitoring-model";

import { todayInTimezone } from "./daily-monitoring-logic";

/** Comment-only "Resolve" dialog for a completed-but-unreviewed row. Posts a
 * physician_notified/regular event and, on success, tells the parent to flip
 * the row to resolved. */
export function ResolveDialog({
  row,
  onResolved,
}: {
  row: MonitoringRow;
  onResolved: () => void;
}) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  // The guard has to be a ref, not the state above: clicks dispatched in one
  // task all run before React re-renders, so each would read `saving === false`
  // from its own closure and write its own event into the patient's history.
  // The state exists only to disable the buttons.
  const savingRef = useRef(false);

  async function confirm() {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      await apiPostEvents({
        body: {
          event_category: EventCategory.physician_notified,
          event_category_type: PhysicianNotifiedEventType.regular,
          event_description: "Daily monitoring review",
          event_comment: comment,
          patient_id: row.patient.patient_id,
          event_date: todayInTimezone(
            Intl.DateTimeFormat().resolvedOptions().timeZone,
          ),
        },
      });
    } catch (error) {
      // Surfaced explicitly rather than left to the global unhandledrejection
      // handler: the dialog stays open with the comment intact so the write can
      // be retried, which only reads correctly if the failure is reported here.
      void handleError(error);
      return;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
    setOpen(false);
    setComment("");
    onResolved();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setComment("");
      }}
    >
      {/* Icon-only, like the Trends and Profile actions beside it: the column
          header names it, and the square footprint (`size-8`) matches theirs. */}
      <DialogTrigger asChild>
        <IconButton
          icon={Check}
          variant="subtle"
          className="size-8 px-0"
          label={t`Resolve`}
          // No hover text, like the Trends and Profile actions: the column
          // header already names it.
          tooltip={null}
          data-testid={`dm-resolve-${row.patient.patient_id}`}
        />
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t`Resolve daily monitoring review`}</DialogTitle>
        </DialogHeader>
        <div className="flex w-96 flex-col gap-2">
          <Label htmlFor={`resolve-comment-${row.patient.patient_id}`}>
            <Trans>Comment</Trans>
          </Label>
          <BaseTextarea
            id={`resolve-comment-${row.patient.patient_id}`}
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2">
          <DialogClose asChild>
            <Button disabled={saving}>
              <Trans>Cancel</Trans>
            </Button>
          </DialogClose>
          <Button disabled={saving} onClick={confirm}>
            <Trans>Confirm</Trans>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
